/**
 * Icon and label presentation for the file types the platform ingests, grouped
 * to mirror backend/src/matter/doc/allowed-extensions.ts. Containers (zip, pst)
 * are unpacked on upload and never shown as documents, so they aren't listed.
 * Icon classes come from the vscode-icons iconify pack (see index.css); the
 * icons carry their own colours, so no text-* classes are needed.
 */
interface FileTypeGroup {
  extensions: string[];
  /** Distinct file-type icon; groups without one use the default file icon */
  icon?: string;
  label: string;
}

const FILE_TYPE_GROUPS: FileTypeGroup[] = [
  { extensions: ['pdf'], icon: 'iconify-color vscode-icons--file-type-pdf2', label: 'PDF document' },
  { extensions: ['doc', 'docx', 'rtf'], icon: 'iconify-color vscode-icons--file-type-word', label: 'Word document' },
  { extensions: ['txt'], label: 'Text document' },
  {
    extensions: ['ppt', 'pptx'],
    icon: 'iconify-color vscode-icons--file-type-powerpoint',
    label: 'PowerPoint presentation',
  },
  {
    extensions: ['xls', 'xlsb', 'xlsx', 'xlsm'],
    icon: 'iconify-color vscode-icons--file-type-excel',
    label: 'Excel spreadsheet',
  },
  // The pack has no dedicated CSV icon; the Excel one is the conventional stand-in
  { extensions: ['csv'], icon: 'iconify-color vscode-icons--file-type-excel', label: 'CSV spreadsheet' },
  { extensions: ['eml', 'msg'], label: 'Email message' },
  {
    extensions: ['htm', 'html', 'mht', 'mhtml'],
    label: 'Web document',
  },
  { extensions: ['json'], label: 'JSON file' },
  {
    extensions: ['img', 'jpg', 'jpeg', 'png', 'tif', 'tiff', 'fax'],
    label: 'Image',
  },
  {
    extensions: [
      'aac',
      'ac3',
      'aif',
      'aiff',
      'amr',
      'awb',
      'caf',
      'dss',
      'flac',
      'm4a',
      'm4r',
      'mp3',
      'oga',
      'ogg',
      'opus',
      'spx',
      'wav',
      'wave',
      'wma',
    ],
    label: 'Audio recording',
  },
  {
    extensions: ['3g2', '3gp', 'avi', 'flv', 'm4v', 'mkv', 'mov', 'mp4', 'mpeg', 'mpg', 'vob', 'webm', 'wmv'],
    label: 'Video recording',
  },
];

const FILE_ICON_FALLBACK = 'iconify-color vscode-icons--default-file';

const DISPLAY_BY_EXTENSION = new Map(
  FILE_TYPE_GROUPS.flatMap((group) =>
    group.extensions.map((extension) => [extension, { icon: group.icon, label: group.label }] as const),
  ),
);

function normaliseExtension(extension: string | null | undefined): string {
  return extension?.toLowerCase().replace(/^\./, '') ?? '';
}

export function getFileIconClass(extension: string | null | undefined): string {
  return DISPLAY_BY_EXTENSION.get(normaliseExtension(extension))?.icon ?? FILE_ICON_FALLBACK;
}

/** Returns an empty string when the extension is missing or unrecognised. */
export function getFileTypeLabel(extension: string | null | undefined): string {
  return DISPLAY_BY_EXTENSION.get(normaliseExtension(extension))?.label ?? '';
}
